/**
 * EXPORTACIONES DE INVENTARIO (F13.5-B)
 *
 *   /inventario/exportaciones                 historial (número, tipo, destino, período, autor, fecha, filas, estado, formato)
 *   /inventario/exportaciones/nueva           asistente: tipo → destino → filtros → vista previa → generar → descargar
 *   /inventario/exportaciones/nueva?reexportar=:id   lo mismo con los parámetros de una exportación anterior (una NUEVA)
 *   /inventario/exportaciones/nueva?cierre=:id       F13.6: stock a la fecha de fin de un cierre, con su ámbito (enlazada a él)
 *   /inventario/exportaciones/:id             ficha: qué se exportó, con qué códigos, hash, avisos, excluidas, reexportaciones
 *
 * Exportar NO mueve stock: sólo registra qué salió hacia qué sistema. El usuario elige con códigos de negocio y
 * nombres (nunca UUID ni tokens); la vista previa es obligatoria y enseña lo que falta («Artículo 100004872 → falta
 * código SAGE») antes de generar. La descarga sale del servidor autenticado y es el fichero registrado (SHA-256).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api, FalloApi, type CierreDetalle, type DestinoExportacion, type ExportacionDetalle, type ExportacionResumen, type FiltrosExportacion, type FormatoExportacion,
  type OpcionAlmacenStock, type OpcionArticuloStock, type OpcionesCsv, type OpcionesExportacion, type OpcionLote, type PreviewExportacion,
  type TipoExportacion,
} from '../api.ts';
import { Error as ErrorCaja, fecha, fechaHora, hoyLocal, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { buscarAlmacenes, buscarArticulos, erroresDe, nombreAlmacen, nombreArticulo } from './stock.tsx';
import { buscarLotes } from './trazabilidad.tsx';

const BASE = '/api/inventario/exportaciones';

export const ETIQUETA_TIPO_EXPORTACION: Readonly<Record<TipoExportacion, string>> = {
  STOCK_ACTUAL: 'Stock actual', STOCK_A_FECHA: 'Stock a fecha', MOVIMIENTOS: 'Movimientos', CONSUMOS: 'Consumos',
};
/** Movimientos y consumos llevan período y filtros de movimiento; el stock (actual o a fecha), no. */
const conPeriodo = (t: TipoExportacion): boolean => t === 'MOVIMIENTOS' || t === 'CONSUMOS';
const AYUDA_TIPO: Readonly<Record<string, string>> = {
  STOCK_ACTUAL: 'Lo que hay ahora en cada almacén, por artículo, lote y serie.',
  STOCK_A_FECHA: 'Lo que había al final de un día (hora de Madrid), calculado desde los movimientos.',
  MOVIMIENTOS: 'Entradas, salidas, traslados, ajustes, devoluciones y consumos de un período.',
  CONSUMOS: 'Sólo el material consumido de verdad (validado) en partes de mantenimiento y OT de limpieza.',
};
const TIPOS_MOVIMIENTO: readonly [string, string][] = [
  ['ENTRADA', 'Entradas'], ['SALIDA', 'Salidas'], ['TRASLADO', 'Traslados'], ['AJUSTE', 'Ajustes'], ['DEVOLUCION', 'Devoluciones'],
  ['CONSUMO_PT', 'Consumos de PT'], ['CONSUMO_OT', 'Consumos de OT'],
];
const TIPOS_CONSUMO: readonly [string, string][] = [['CONSUMO_PT', 'Partes de mantenimiento'], ['CONSUMO_OT', 'OT de limpieza']];
const SEPARADORES: readonly [OpcionesCsv['separador'], string][] = [[';', 'Punto y coma (;)'], [',', 'Coma (,)'], ['\t', 'Tabulador'], ['|', 'Barra (|)']];

function periodoTexto(p: { desde: string; hasta: string } | null): string {
  return p === null ? 'Actual' : p.desde === p.hasta ? fecha(p.desde) : `${fecha(p.desde)} – ${fecha(p.hasta)}`;
}
const nombreDestino = (d: DestinoExportacion): string => (d.codigo === 'NEATOS_ESTANDAR' ? 'NeatOS estándar' : d.codigo === d.nombre ? d.codigo : `${d.codigo} · ${d.nombre}`);
const plural = (n: number, uno: string, varios: string): string => `${String(n)} ${n === 1 ? uno : varios}`;

/** Mes completo (días locales) que contiene `dia` desplazado `meses`. */
function mes(dia: string, meses: number): { desde: string; hasta: string } {
  const [a, m] = dia.split('-').map(Number) as [number, number];
  const ini = new Date(a, m - 1 + meses, 1);
  const fin = new Date(a, m + meses, 0);
  return { desde: hoyLocal(0, ini), hasta: hoyLocal(0, fin) };
}

export function guardarFichero(blob: Blob, nombre: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Botón «Descargar»: el fichero sale del servidor (autenticado) y es el registrado. */
export function BotonDescargar({ id, principal = false, texto = 'Descargar' }: { id: string; principal?: boolean; texto?: string }): ReactNode {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function descargar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      const f = await api.fichero(`${BASE}/${id}/descargar`);
      guardarFichero(f.blob, f.nombre);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  return (
    <>
      <button type="button" className={principal ? 'principal' : 'pequeno'} disabled={ocupado} onClick={() => void descargar()}>
        {ocupado ? 'Descargando…' : texto}</button>
      {error !== null && <ErrorCaja error={error} />}
    </>
  );
}

function EstadoExportacion({ estado }: { estado: 'GENERADA' | 'ANULADA' }): ReactNode {
  return estado === 'GENERADA' ? <span className="etiqueta exito">Generada</span> : <span className="etiqueta neutra">Anulada</span>;
}

// ================================================================== historial
export function PaginaExportaciones(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [tipo, setTipo] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(1);
  const lista = useDatos(() => api.get<{ filas: ExportacionResumen[]; hayMas: boolean }>(
    `${BASE}?${new URLSearchParams({ tipo, estado, pagina: String(pagina), porPagina: '25' }).toString()}`), [tipo, estado, pagina]);
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Exportaciones' }]} titulo="Historial de exportaciones"
      subtitulo="Stock, movimientos y consumos para SAP, Sage u otro sistema. Exportar no mueve stock: sólo registra qué salió."
      acciones={puede('exportaciones_inventario:crear')
        ? <button type="button" className="principal" onClick={() => navegar('/inventario/exportaciones/nueva')}>Nueva exportación</button> : undefined}>
      <div className="panel">
        <div className="panel-cuerpo form-rejilla">
          <div className="campo"><label htmlFor="exp-f-tipo">Tipo</label>
            <select id="exp-f-tipo" value={tipo} onChange={(ev) => { setTipo(ev.target.value); setPagina(1); }}>
              <option value="">Todos</option>
              {(Object.keys(ETIQUETA_TIPO_EXPORTACION) as TipoExportacion[]).map((t) => <option key={t} value={t}>{ETIQUETA_TIPO_EXPORTACION[t]}</option>)}
            </select></div>
          <div className="campo"><label htmlFor="exp-f-estado">Estado</label>
            <select id="exp-f-estado" value={estado} onChange={(ev) => { setEstado(ev.target.value); setPagina(1); }}>
              <option value="">Todos</option><option value="GENERADA">Generadas</option><option value="ANULADA">Anuladas</option>
            </select></div>
        </div>
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? (
            <Vacio titulo="Todavía no hay exportaciones">
              {puede('exportaciones_inventario:crear') && <Link to="/inventario/exportaciones/nueva">Haz la primera</Link>}
            </Vacio>
          ) : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Exportaciones" className="tabla-tarjetas">
                  <thead><tr><th>Número</th><th>Tipo</th><th>Destino</th><th>Período</th><th>Creado por</th><th>Fecha</th>
                    <th>Filas</th><th>Estado</th><th>Formato</th><th>Fichero</th></tr></thead>
                  <tbody>
                    {d.filas.map((e) => (
                      <tr key={e.id}>
                        <td className="celda-titulo"><Link className="mono" to={`/inventario/exportaciones/${e.id}`}>{e.numero}</Link>
                          {e.reexportacionDe !== null && <div className="pequena suave">Reexporta {e.reexportacionDe.numero}</div>}</td>
                        <td data-label="Tipo">{ETIQUETA_TIPO_EXPORTACION[e.tipo]}</td>
                        <td data-label="Destino">{nombreDestino(e.destino)}</td>
                        <td data-label="Período">{periodoTexto(e.periodo)}</td>
                        <td data-label="Creado por">{e.creadoPor.nombre}</td>
                        <td data-label="Fecha">{fechaHora(e.creadoEn)}</td>
                        <td data-label="Filas">{e.numFilas}{e.numExcluidas > 0 && <span className="pequena suave"> ({e.numExcluidas} excl.)</span>}</td>
                        <td data-label="Estado"><EstadoExportacion estado={e.estado} /></td>
                        <td data-label="Formato">{e.formato === 'XLSX' ? 'Excel' : 'CSV'}</td>
                        <td data-label="Fichero"><BotonDescargar id={e.id} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <nav className="fila" aria-label="Páginas">
                <button type="button" className="pequeno" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
                <span className="suave pequena">Página {pagina}</span>
                <button type="button" className="pequeno" disabled={!d.hayMas} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
              </nav>
            </>
          ))}
        </Panel>
      </div>
    </Shell>
  );
}

// ================================================================== asistente
interface Formulario {
  tipo: TipoExportacion;
  idDestino: string | null;
  desde: string;
  hasta: string;
  idCentro: string;
  almacen: OpcionAlmacenStock | null;
  articulo: OpcionArticuloStock | null;
  lote: OpcionLote | null;
  idFamilia: string;
  tipos: string[];
  tipoDocumento: string;
  exportado: '' | 'SI' | 'NO';
  formato: FormatoExportacion;
  csv: OpcionesCsv;
  /** STOCK_A_FECHA: fecha de corte. */
  fecha: string;
  /** F13.6: exportación desde un cierre (fecha y ámbito fijos). */
  cierre: { id: string; nombre: string; ambito: string; idCentro?: string; idAlmacen?: string } | null;
}

function cuerpoDe(f: Formulario): Record<string, unknown> {
  const filtros: FiltrosExportacion = {};
  if (f.cierre !== null) {
    if (f.cierre.idCentro !== undefined) filtros.idCentro = f.cierre.idCentro;
    if (f.cierre.idAlmacen !== undefined) filtros.idAlmacen = f.cierre.idAlmacen;
  } else {
    if (f.idCentro !== '') filtros.idCentro = f.idCentro;
    if (f.almacen !== null) filtros.idAlmacen = f.almacen.id;
  }
  if (f.articulo !== null) filtros.idArticulo = f.articulo.id;
  if (f.lote !== null) filtros.idLote = f.lote.id;
  if (f.idFamilia !== '') filtros.idFamilia = f.idFamilia;
  if (conPeriodo(f.tipo)) {
    if (f.tipos.length > 0) filtros.tipos = f.tipos;
    if (f.tipoDocumento.trim() !== '') filtros.tipoDocumento = f.tipoDocumento.trim();
    if (f.exportado !== '') filtros.exportado = f.exportado;
  }
  return {
    tipo: f.tipo, idSistemaDestino: f.idDestino, filtros, formato: f.formato,
    ...(conPeriodo(f.tipo) ? { desde: f.desde, hasta: f.hasta } : {}),
    ...(f.tipo === 'STOCK_A_FECHA' ? { fecha: f.fecha, ...(f.cierre === null ? {} : { idCierre: f.cierre.id }) } : {}),
    ...(f.formato === 'CSV' ? { opciones: f.csv } : {}),
  };
}

function Paso({ n, titulo, children }: { n: number; titulo: string; children: ReactNode }): ReactNode {
  return (
    <section className="panel" aria-labelledby={`paso-${String(n)}`}>
      <div className="panel-cuerpo">
        <h2 id={`paso-${String(n)}`} className="titulo-seccion"><span className="etiqueta info">Paso {n}</span> {titulo}</h2>
        {children}
      </div>
    </section>
  );
}

type Requisito = 'OBLIGATORIA' | 'INFORMATIVA';
const requisitosDe = (r: Record<string, unknown>): Record<string, Requisito> => (r['requisitos'] ?? {}) as Record<string, Requisito>;
/** Qué campos del layout necesitan el código propio del destino (obligatorios) y cuáles pueden llevar el de NeatOS. */
function Requisitos({ requisitos, destino }: { requisitos: Record<string, Requisito>; destino: string }): ReactNode {
  const campos = Object.entries(requisitos);
  if (campos.length === 0) return null;
  const de = (r: Requisito) => campos.filter(([, v]) => v === r).map(([k]) => k).join(', ');
  return (
    <p className="pequena suave">Código propio de {destino} obligatorio en: <span className="mono">{de('OBLIGATORIA')}</span>
      {de('INFORMATIVA') !== '' && <>; informativo (si falta, va el de NeatOS): <span className="mono">{de('INFORMATIVA')}</span></>}.</p>
  );
}

/** Lo que se va a exportar, antes de generar: filas, errores (qué falta), avisos y totales. */
function VistaPrevia({ p }: { p: PreviewExportacion }): ReactNode {
  const cols = p.columnas.filter((c) => !c.startsWith('token_')).slice(0, 12);
  return (
    <div aria-label="Vista previa">
      <p className="fila">
        <strong>{plural(p.totalFilas, 'fila', 'filas')}</strong>
        <span className="etiqueta exito">{plural(p.filasValidas, 'válida', 'válidas')}</span>
        {p.filasConError > 0 && <span className="etiqueta peligro">{plural(p.filasConError, 'con error', 'con error')}</span>}
        <span className="suave pequena">{p.periodoTexto} · destino {nombreDestino(p.destino)} · {p.versionLayout}</span>
      </p>
      {p.filtrosTexto.length > 0 && <p className="pequena suave">Filtros: {p.filtrosTexto.map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>}
      <Requisitos requisitos={requisitosDe(p.resolucion)} destino={p.destino.codigo} />
      {p.errores.length > 0 && (
        <div className="nota error" role="alert">
          <strong>Faltan códigos en {p.destino.codigo}.</strong> Estas filas no pueden salir con un código inventado:
          <ul>{p.errores.map((e) => <li key={`${e.entidad}:${e.id}`}>{e.mensaje} <span className="suave">(campo <span className="mono">{e.campo}</span> · {plural(e.filas, 'fila', 'filas')})</span></li>)}</ul>
          Corrígelo (referencia del artículo, código de negocio del almacén o equivalencia de familia o unidad en el sistema), o exclúyelas expresamente al generar.
        </div>
      )}
      {p.avisos.length > 0 && (
        <div className="nota aviso" role="status">
          <ul>{p.avisos.map((a) => <li key={a.codigo}>{a.mensajes[0] ?? a.codigo}{a.mensajes.length > 1 && ` (y ${String(a.mensajes.length - 1)} más)`} <span className="suave">· {plural(a.filas, 'fila', 'filas')}</span></li>)}</ul>
        </div>
      )}
      {p.totales.length > 0 && (
        <p className="pequena">Totales: {p.totales.map((t) => `${t.cantidad.replace('.', ',')} ${t.unidad}`).join(' · ')}</p>
      )}
      {p.muestra.length === 0 ? <Vacio titulo="No hay nada que exportar con estos filtros" /> : (
        <div className="tabla-desliza">
          <table aria-label="Muestra de la exportación" className="tabla-compacta">
            <thead><tr>{cols.map((c) => <th key={c} className="mono pequena">{c}</th>)}</tr></thead>
            <tbody>
              {p.muestra.map((m, i) => (
                <tr key={i} className={m.conError ? 'fila-error' : undefined}>
                  {cols.map((c) => <td key={c} className={c.startsWith('codigo') ? 'mono' : undefined}>
                    {m.valores[c] ?? (m.conError && (c === 'codigo_articulo' || c.startsWith('codigo_almacen')) ? <span className="etiqueta peligro">falta</span> : '')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {p.totalFilas > p.muestra.length && <p className="pequena suave">Se muestran las {p.muestra.length} primeras de {p.totalFilas}.</p>}
        </div>
      )}
    </div>
  );
}

export function PaginaNuevaExportacion(): ReactNode {
  const { puede } = useSesion();
  const [params] = useSearchParams();
  const idReexportar = params.get('reexportar');
  const idCierre = params.get('cierre');
  //  F13.7: el asistente se abre desde Movimientos («Exportar» → MOVIMIENTOS) o desde Stock (STOCK_ACTUAL, o
  //  STOCK_A_FECHA con su fecha); sigue siendo el mismo asistente con vista previa obligatoria.
  const tipoPedido = params.get('tipo');
  const fechaPedida = params.get('fecha');
  const opciones = useDatos(() => api.get<OpcionesExportacion>(`${BASE}/opciones`), []);
  const original = useDatos(() => (idReexportar === null ? Promise.resolve(null) : api.get<ExportacionDetalle>(`${BASE}/${idReexportar}`)), [idReexportar]);
  const cierre = useDatos(() => (idCierre === null ? Promise.resolve(null) : api.get<CierreDetalle>(`/api/inventario/cierres/${idCierre}`)), [idCierre]);
  const hoy = hoyLocal();
  const [f, setF] = useState<Formulario>(() => ({
    tipo: idCierre !== null ? 'STOCK_A_FECHA'
      : tipoPedido !== null && (Object.keys(ETIQUETA_TIPO_EXPORTACION) as string[]).includes(tipoPedido) ? tipoPedido as TipoExportacion : 'MOVIMIENTOS',
    idDestino: null, ...mes(hoy, -1), idCentro: '', almacen: null, articulo: null, lote: null,
    idFamilia: '', tipos: [], tipoDocumento: '', exportado: '', formato: 'XLSX', csv: { separador: ';', decimal: ',', bom: true },
    fecha: fechaPedida !== null && /^\d{4}-\d{2}-\d{2}$/.test(fechaPedida) && fechaPedida <= hoy ? fechaPedida : hoy, cierre: null,
  }));
  const [destinoElegido, setDestinoElegido] = useState(false);
  const [preview, setPreview] = useState<PreviewExportacion | null>(null);
  const [excluir, setExcluir] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [creada, setCreada] = useState<ExportacionDetalle | null>(null);
  const campos = erroresDe(error);

  //  Destino por defecto: el maestro de artículos (si hay); si no, NeatOS estándar.
  useEffect(() => {
    const d = opciones.datos;
    if (d === null || destinoElegido) return;
    const maestro = d.destinos.find((x) => x.maestroDe?.includes('ARTICULOS') === true);
    if (maestro !== undefined) setF((v) => ({ ...v, idDestino: maestro.id }));
  }, [opciones.datos, destinoElegido]);
  //  F13.6: desde un cierre, el stock a su fecha de fin y con su ámbito (no se cambian aquí).
  useEffect(() => {
    const c = cierre.datos;
    if (c === null) return;
    setF((v) => ({ ...v, tipo: 'STOCK_A_FECHA', fecha: c.hasta, cierre: { id: c.id, nombre: `${c.codigo} v${String(c.version)}`, ambito: c.ambitoTexto,
      ...(c.filtros.idCentro === undefined ? {} : { idCentro: c.filtros.idCentro }), ...(c.filtros.idAlmacen === undefined ? {} : { idAlmacen: c.filtros.idAlmacen }) } }));
  }, [cierre.datos]);
  useEffect(() => {
    const o = original.datos;
    if (o === null) return;
    setF((v) => ({ ...v, tipo: o.tipo, idDestino: o.destino.id, formato: o.formato, ...(o.periodo ?? {}),
      csv: { ...v.csv, ...o.opciones } }));
  }, [original.datos]);

  const cambiar = (c: Partial<Formulario>): void => {
    setF((v) => ({ ...v, ...c }));
    setPreview(null);
    setExcluir(false);
    setError(null);
  };
  const reexporta = idReexportar !== null;
  const ruta = (accion: 'preview' | 'crear'): string => (reexporta
    ? `${BASE}/${idReexportar}/reexportar${accion === 'preview' ? '/preview' : ''}`
    : accion === 'preview' ? `${BASE}/preview` : BASE);
  const cuerpo = reexporta ? { formato: f.formato, ...(f.formato === 'CSV' ? { opciones: f.csv } : {}) } : cuerpoDe(f);

  async function previsualizar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      setPreview(await api.post<PreviewExportacion>(ruta('preview'), cuerpo));
      setExcluir(false);
    } catch (e) {
      setPreview(null);
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  async function generar(): Promise<void> {
    if (preview === null) return;
    setError(null);
    setOcupado(true);
    try {
      setCreada(await api.post<ExportacionDetalle>(ruta('crear'), { ...cuerpo, huella: preview.huella, excluirConErrores: excluir }));
    } catch (e) {
      if (e instanceof FalloApi && e.codigo === 'PREVIEW_CADUCADO') setPreview(null);
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  const migas = [{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Exportaciones', a: '/inventario/exportaciones' }, { texto: reexporta ? 'Reexportar' : 'Nueva' }];
  if (!puede('exportaciones_inventario:crear')) {
    return <Shell migas={migas} titulo="Exportación no disponible"><div className="panel"><Vacio titulo="Tu perfil no puede exportar"><Link to="/inventario/exportaciones">Volver</Link></Vacio></div></Shell>;
  }
  if (creada !== null) {
    return (
      <Shell dominio="movimientos" migas={migas} titulo={`Exportación ${creada.numero}`} subtitulo="Generada. El fichero queda registrado con su huella SHA-256.">
        <Paso n={6} titulo="Descargar">
          <p><strong>{plural(creada.numFilas, 'fila', 'filas')}</strong> · {ETIQUETA_TIPO_EXPORTACION[creada.tipo]} · {nombreDestino(creada.destino)} · {periodoTexto(creada.periodo)}
            {creada.numExcluidas > 0 && <> · <span className="etiqueta aviso">{plural(creada.numExcluidas, 'fila excluida', 'filas excluidas')}</span></>}</p>
          <p className="pequena suave mono" style={{ overflowWrap: 'anywhere' }}>{creada.nombreFichero} · SHA-256 {creada.sha256}</p>
          <div className="fila">
            <BotonDescargar id={creada.id} principal texto={`Descargar ${creada.formato === 'XLSX' ? 'Excel' : 'CSV'}`} />
            <Link to={`/inventario/exportaciones/${creada.id}`}>Ver la exportación</Link>
            <Link to="/inventario/exportaciones">Volver al historial</Link>
          </div>
        </Paso>
      </Shell>
    );
  }
  return (
    <Shell dominio="movimientos" migas={migas} titulo={reexporta ? 'Reexportar' : 'Nueva exportación'}
      subtitulo={reexporta ? 'Se genera una exportación NUEVA con los datos y códigos de hoy; la anterior no cambia.' : 'Exportar no mueve stock ni marca nada como consumido.'}>
      <Panel estado={opciones}>
        {(o) => (
          <>
            {reexporta && (
              <Panel estado={original}>
                {(x) => (x === null ? null : (
                  <div className="nota info" role="note">
                    Reexportación de <Link className="mono" to={`/inventario/exportaciones/${x.id}`}>{x.numero}</Link>: {ETIQUETA_TIPO_EXPORTACION[x.tipo]} · {nombreDestino(x.destino)} · {periodoTexto(x.periodo)}
                    {x.filtrosTexto.length > 0 && <> · {x.filtrosTexto.map(([k, v]) => `${k}: ${v}`).join(' · ')}</>}.
                    {x.filtros.exportado !== undefined && ' Se reenvía todo lo del período (sin el filtro «exportado / no exportado»).'}
                  </div>
                ))}
              </Panel>
            )}
            {f.cierre !== null && (
              <div className="nota info" role="note">
                Stock a fecha del cierre <Link className="mono" to={`/inventario/cierres/${f.cierre.id}`}>{f.cierre.nombre}</Link>: al final del {fecha(f.fecha)} (hora de Madrid) · {f.cierre.ambito}.
                Sale del ledger (el mismo cálculo que el cierre) y queda enlazada a él.
              </div>
            )}
            {!reexporta && (
              <>
                {f.cierre === null && <Paso n={1} titulo="Tipo">
                  <div className="opciones-tarjeta" role="radiogroup" aria-label="Tipo de exportación">
                    {o.tipos.map((t) => (
                      <label key={t.tipo} className={`opcion-tarjeta${f.tipo === t.tipo ? ' activa' : ''}${t.disponible ? '' : ' deshabilitada'}`}>
                        <input type="radio" name="tipo" value={t.tipo} checked={f.tipo === t.tipo} disabled={!t.disponible}
                          onChange={() => cambiar({ tipo: t.tipo as TipoExportacion, tipos: [], exportado: '' })} />
                        <strong>{t.etiqueta}</strong>
                        <span className="pequena suave">{t.disponible ? AYUDA_TIPO[t.tipo] : t.motivo}</span>
                      </label>
                    ))}
                  </div>
                </Paso>}
                <Paso n={2} titulo="Destino">
                  <div className="opciones-tarjeta" role="radiogroup" aria-label="Sistema de destino">
                    {o.destinos.map((d) => (
                      <label key={d.codigo} className={`opcion-tarjeta${f.idDestino === d.id ? ' activa' : ''}`}>
                        <input type="radio" name="destino" checked={f.idDestino === d.id} onChange={() => { setDestinoElegido(true); cambiar({ idDestino: d.id }); }} />
                        <strong>{nombreDestino(d)}</strong>
                        <span className="pequena suave">{d.id === null ? 'Códigos de negocio y, aparte, los identificadores técnicos de NeatOS.'
                          : d.maestroDe !== undefined && d.maestroDe.includes('ARTICULOS') ? 'Sistema maestro de artículos: sale el código de negocio.'
                            : `Sale el código que cada artículo tiene en ${d.codigo}.`}</span>
                      </label>
                    ))}
                  </div>
                </Paso>
                <Paso n={3} titulo="Filtros">
                  {f.tipo === 'STOCK_A_FECHA' && f.cierre === null && (
                    <>
                      <div className="form-rejilla">
                        <div className="campo"><label htmlFor="exp-fecha">Fecha de corte</label>
                          <input id="exp-fecha" type="date" max={hoy} value={f.fecha} onChange={(ev) => cambiar({ fecha: ev.target.value })} />
                          {campos['fecha'] !== undefined && <span className="error-campo">{campos['fecha']}</span>}</div>
                      </div>
                      <p className="pequena suave">Stock al final de ese día en hora de Madrid (23:59:59), según la fecha real de cada movimiento.</p>
                    </>
                  )}
                  {conPeriodo(f.tipo) && (
                    <>
                      <div className="fila" role="group" aria-label="Período rápido">
                        <button type="button" className="pequeno" onClick={() => cambiar(mes(hoy, 0))}>Este mes</button>
                        <button type="button" className="pequeno" onClick={() => cambiar(mes(hoy, -1))}>Mes anterior</button>
                      </div>
                      <div className="form-rejilla">
                        <div className="campo"><label htmlFor="exp-desde">Desde</label>
                          <input id="exp-desde" type="date" value={f.desde} onChange={(ev) => cambiar({ desde: ev.target.value })} />
                          {campos['desde'] !== undefined && <span className="error-campo">{campos['desde']}</span>}</div>
                        <div className="campo"><label htmlFor="exp-hasta">Hasta</label>
                          <input id="exp-hasta" type="date" value={f.hasta} onChange={(ev) => cambiar({ hasta: ev.target.value })} />
                          {campos['hasta'] !== undefined && <span className="error-campo">{campos['hasta']}</span>}</div>
                      </div>
                      <p className="pequena suave">Días completos en hora de Madrid (incluido el cambio de hora).</p>
                    </>
                  )}
                  <div className="form-rejilla">
                    {f.cierre === null && (
                      <>
                        <div className="campo"><label htmlFor="exp-centro">Centro</label>
                          <select id="exp-centro" value={f.idCentro} onChange={(ev) => cambiar({ idCentro: ev.target.value })}>
                            <option value="">Todos</option>
                            {o.centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.descripcion}</option>)}
                          </select></div>
                        <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={f.almacen} alElegir={(a) => cambiar({ almacen: a })} buscar={buscarAlmacenes}
                          clave={(a) => a.id} texto={nombreAlmacen} placeholder="Todos" />
                      </>
                    )}
                    <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={f.articulo} alElegir={(a) => cambiar({ articulo: a, lote: null })}
                      buscar={buscarArticulos(null)} clave={(a) => a.id} texto={nombreArticulo} placeholder="Todos" />
                    {f.articulo !== null && f.articulo.trazabilidad?.controlaLotes === true && (
                      <Buscador<OpcionLote> etiqueta="Lote" valor={f.lote} alElegir={(l) => cambiar({ lote: l })} buscar={buscarLotes({ idArticulo: f.articulo.id })}
                        clave={(l) => l.id} texto={(l) => l.codigo} contexto={f.articulo.id} placeholder="Todos" />
                    )}
                    <div className="campo"><label htmlFor="exp-familia">Familia</label>
                      <select id="exp-familia" value={f.idFamilia} onChange={(ev) => cambiar({ idFamilia: ev.target.value })}>
                        <option value="">Todas</option>
                        {o.familias.map((x) => <option key={x.id} value={x.id}>{x.codigo} · {x.descripcion}</option>)}
                      </select></div>
                    {conPeriodo(f.tipo) && (
                      <>
                        <div className="campo"><label htmlFor="exp-tipo-doc">Tipo de documento</label>
                          <input id="exp-tipo-doc" value={f.tipoDocumento} maxLength={40} placeholder="Albarán, factura…" onChange={(ev) => cambiar({ tipoDocumento: ev.target.value })} /></div>
                        <div className="campo"><label htmlFor="exp-exportado">Estado administrativo</label>
                          <select id="exp-exportado" value={f.exportado} onChange={(ev) => cambiar({ exportado: ev.target.value as Formulario['exportado'] })}>
                            <option value="">Todo</option><option value="NO">Sólo lo nunca exportado a este destino</option><option value="SI">Sólo lo ya exportado</option>
                          </select></div>
                      </>
                    )}
                  </div>
                  {conPeriodo(f.tipo) && (
                    <fieldset className="tipos-exportacion">
                      <legend>{f.tipo === 'CONSUMOS' ? 'Origen del consumo' : 'Tipos de movimiento'} <span className="suave pequena">(ninguno = todos)</span></legend>
                      <div className="fila">
                        {(f.tipo === 'CONSUMOS' ? TIPOS_CONSUMO : TIPOS_MOVIMIENTO).map(([k, t]) => (
                          <label key={k} className="casilla-exportacion"><input type="checkbox" checked={f.tipos.includes(k)}
                            onChange={(ev) => cambiar({ tipos: ev.target.checked ? [...f.tipos, k] : f.tipos.filter((x) => x !== k) })} /> {t}</label>
                        ))}
                      </div>
                    </fieldset>
                  )}
                </Paso>
              </>
            )}
            <Paso n={reexporta ? 1 : 4} titulo="Formato y vista previa">
              <div className="form-rejilla">
                <div className="campo"><label htmlFor="exp-formato">Formato</label>
                  <select id="exp-formato" value={f.formato} onChange={(ev) => cambiar({ formato: ev.target.value as FormatoExportacion })}>
                    <option value="XLSX">Excel (XLSX)</option><option value="CSV">CSV</option>
                  </select></div>
                {f.formato === 'CSV' && (
                  <>
                    <div className="campo"><label htmlFor="exp-separador">Separador</label>
                      <select id="exp-separador" value={f.csv.separador} onChange={(ev) => cambiar({ csv: { ...f.csv, separador: ev.target.value as OpcionesCsv['separador'] } })}>
                        {SEPARADORES.map(([v, t]) => <option key={t} value={v}>{t}</option>)}
                      </select></div>
                    <div className="campo"><label htmlFor="exp-decimal">Decimal</label>
                      <select id="exp-decimal" value={f.csv.decimal} onChange={(ev) => cambiar({ csv: { ...f.csv, decimal: ev.target.value as OpcionesCsv['decimal'] } })}>
                        <option value=",">Coma (12,5)</option><option value=".">Punto (12.5)</option>
                      </select>
                      {campos['opciones.decimal'] !== undefined && <span className="error-campo">{campos['opciones.decimal']}</span>}</div>
                    <label className="casilla-exportacion"><input type="checkbox" checked={f.csv.bom} onChange={(ev) => cambiar({ csv: { ...f.csv, bom: ev.target.checked } })} /> UTF-8 con BOM (Excel)</label>
                  </>
                )}
              </div>
              <div className="fila acciones-paso"><button type="button" className={preview === null ? 'principal' : ''} disabled={ocupado} onClick={() => void previsualizar()}>
                {ocupado && preview === null ? 'Calculando…' : preview === null ? 'Ver vista previa' : 'Actualizar vista previa'}</button></div>
              {error !== null && (Object.keys(campos).length === 0 || preview === null) && <ErrorCaja error={error} />}
              {preview !== null && <VistaPrevia p={preview} />}
            </Paso>
            {preview !== null && (
              <Paso n={reexporta ? 2 : 5} titulo="Generar">
                {preview.filasConError > 0 && (
                  <label className="casilla-exportacion"><input type="checkbox" checked={excluir} onChange={(ev) => setExcluir(ev.target.checked)} />{' '}
                    Excluir las {plural(preview.filasConError, 'fila', 'filas')} con errores (quedan registradas en la exportación)</label>
                )}
                <div className="fila">
                  <button type="button" className="principal" disabled={ocupado || (preview.filasConError > 0 && !excluir)} onClick={() => void generar()}>
                    {ocupado ? 'Generando…' : `Generar ${f.formato === 'XLSX' ? 'Excel' : 'CSV'} (${plural(preview.filasValidas, 'fila', 'filas')})`}</button>
                  <span className="pequena suave">No mueve stock. Queda en el historial con su huella SHA-256.</span>
                </div>
              </Paso>
            )}
          </>
        )}
      </Panel>
    </Shell>
  );
}

// ================================================================== ficha
function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return <div className="dato"><div className="etiqueta-dato">{e}</div><div className="valor-dato">{v ?? '—'}</div></div>;
}
const DOMINIOS: readonly [string, string][] = [['ARTICULOS', 'artículos'], ['ALMACENES', 'almacenes'], ['FAMILIAS', 'familias'], ['UNIDADES', 'unidades']];
const RESOLUCION: Readonly<Record<string, string>> = {
  CODIGO_NEGOCIO: 'código de negocio', REFERENCIA_DESTINO: 'referencia del destino', EQUIVALENCIA_DESTINO: 'equivalencia del destino',
};

export function PaginaExportacion(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const d = useDatos(() => api.get<ExportacionDetalle>(`${BASE}/${id}`), [id]);
  const [anular, setAnular] = useState(false);
  const titulo = d.datos?.numero ?? 'Exportación';
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Exportaciones', a: '/inventario/exportaciones' }, { texto: titulo }]} titulo={titulo}
      subtitulo={d.datos === null ? undefined : `${ETIQUETA_TIPO_EXPORTACION[d.datos.tipo]} · ${nombreDestino(d.datos.destino)} · ${periodoTexto(d.datos.periodo)}`}>
      <Panel estado={d}>
        {(x) => (
          <>
            <div className="panel"><div className="panel-cuerpo">
              <div className="fila">
                <EstadoExportacion estado={x.estado} />
                <BotonDescargar id={x.id} principal texto={`Descargar ${x.formato === 'XLSX' ? 'Excel' : 'CSV'}`} />
                {puede('exportaciones_inventario:crear') && (
                  <button type="button" onClick={() => navegar(`/inventario/exportaciones/nueva?reexportar=${x.id}`)}>Reexportar</button>
                )}
                {x.puedeAnular && <button type="button" className="peligro" onClick={() => setAnular(true)}>Anular</button>}
              </div>
              {x.anulacion !== null && (
                <p className="nota aviso" role="status">Anulada por {x.anulacion.por} el {fechaHora(x.anulacion.en)}: {x.anulacion.motivo}. No cambió el stock; el fichero sigue disponible.</p>
              )}
              <div className="datos">
                <Dato e="Filas" v={<>{x.numFilas}{x.numExcluidas > 0 && <span className="suave"> · {plural(x.numExcluidas, 'excluida', 'excluidas')}</span>}</>} />
                <Dato e="Creada" v={`${fechaHora(x.creadoEn)} · ${x.creadoPor.nombre}`} />
                <Dato e="Fichero" v={<span className="mono pequena" style={{ overflowWrap: 'anywhere' }}>{x.nombreFichero}</span>} />
                <Dato e="Layout" v={x.versionLayout} />
                <Dato e="Ámbito" v={x.ambito.global ? 'Todos los centros' : x.ambito.centros.map((c) => c.codigo).join(', ') || 'Sin centros'} />
                {x.reexportacionDe !== null && <Dato e="Reexporta" v={<Link className="mono" to={`/inventario/exportaciones/${x.reexportacionDe.id}`}>{x.reexportacionDe.numero}</Link>} />}
                {x.cierre != null && <Dato e="Cierre" v={<Link className="mono" to={`/inventario/cierres/${x.cierre.id}`}>{x.cierre.codigo} v{x.cierre.version}</Link>} />}
                <Dato e="Filtros" v={x.filtrosTexto.length === 0 ? 'Ninguno' : x.filtrosTexto.map(([k, v]) => `${k}: ${v}`).join(' · ')} />
                <Dato e="Códigos" v={<>{DOMINIOS.map(([k, t]) => `${t}: ${RESOLUCION[String(x.resolucion[k])] ?? '—'}`).join(' · ')}
                  <Requisitos requisitos={requisitosDe(x.resolucion)} destino={x.destino.codigo} /></>} />
              </div>
              {(x.resumen.excluidas ?? []).length > 0 && (
                <div className="nota error" role="note"><strong>Excluido (faltaban códigos):</strong>
                  <ul>{(x.resumen.excluidas ?? []).map((e) => <li key={`${e.entidad}:${e.id}`}>{e.mensaje} <span className="suave">(campo <span className="mono">{e.campo}</span> · {plural(e.filas, 'fila', 'filas')})</span></li>)}</ul></div>
              )}
              {(x.resumen.avisos ?? []).length > 0 && (
                <div className="nota aviso" role="note"><ul>{(x.resumen.avisos ?? []).map((a) => <li key={a.codigo}>{a.mensajes[0] ?? a.codigo} <span className="suave">· {plural(a.filas, 'fila', 'filas')}</span></li>)}</ul></div>
              )}
              {x.reexportaciones.length > 0 && (
                <p className="pequena">Reexportada en: {x.reexportaciones.map((r, i) => <span key={r.id}>{i > 0 && ', '}<Link className="mono" to={`/inventario/exportaciones/${r.id}`}>{r.numero}</Link></span>)}</p>
              )}
              <details className="detalles-tecnicos">
                <summary>Detalles técnicos</summary>
                <p className="pequena mono" style={{ overflowWrap: 'anywhere' }}>SHA-256 del fichero: {x.sha256}<br />Huella de los datos: {x.huellaDatos}<br />{x.tamanoBytes} bytes</p>
              </details>
            </div></div>
            <div className="panel"><div className="panel-cuerpo">
              <h2 className="titulo-seccion">Contenido exportado {x.numFilas > x.muestra.length && <span className="pequena suave">(las {x.muestra.length} primeras de {x.numFilas})</span>}</h2>
              {x.muestra.length === 0 ? <Vacio titulo="Exportación sin filas" /> : (
                <div className="tabla-desliza">
                  <table aria-label="Contenido exportado" className="tabla-compacta">
                    <thead><tr>{x.columnas.map((c) => <th key={c} className="mono pequena">{c}</th>)}</tr></thead>
                    <tbody>{x.muestra.map((v, i) => <tr key={i}>{x.columnas.map((c) => <td key={c} className={c.startsWith('codigo') ? 'mono' : undefined}>{v[c] ?? ''}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              )}
            </div></div>
            {anular && <DialogoAnular id={x.id} numero={x.numero} cerrar={() => setAnular(false)} alAnular={() => { setAnular(false); d.recargar(); }} />}
          </>
        )}
      </Panel>
    </Shell>
  );
}

function DialogoAnular({ id, numero, cerrar, alAnular }: { id: string; numero: string; cerrar: () => void; alAnular: () => void }): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const campos = useMemo(() => erroresDe(error), [error]);
  async function confirmar(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      await api.post(`${BASE}/${id}/anular`, { motivo });
      alAnular();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  return (
    <Modal titulo={`Anular ${numero}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="peligro" disabled={ocupado || motivo.trim().length < 5} onClick={() => void confirmar()}>Anular</button></>}>
      <p>Anular es administrativo: no toca el stock ni borra el fichero ni el historial. Lo anulado deja de contar como «exportado».</p>
      <div className="campo"><label htmlFor="exp-motivo">Motivo</label>
        <textarea id="exp-motivo" rows={2} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        {campos['motivo'] !== undefined && <span className="error-campo">{campos['motivo']}</span>}</div>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
    </Modal>
  );
}
